/* eslint-disable no-unused-vars */
import { Button, FModal, Icons, LoadingIndicator, Checkbox } from '@components';
import {
  dadosContatoSchema,
  dadosPessoaisSchema,
  enderecoSchema,
} from '@schemas';
import { plans } from '@utils';
import { format, parse } from 'date-fns';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { mdiAlertCircleOutline, mdiCheckCircleOutline } from '@mdi/js';
import { DadosPessoaisService } from '../../common/services/dados-pessoais';
import { SOLICITACAO_SUCESSO } from '../../common/utils/paths';

import { DadosPessoaisForm } from './components/DadosPessoais/DadosPessoaisForm';
import { DadosContatoForm } from './components/Contato/DadosContatoForm';
import { DadosResidenciaisForm } from './components/Residenciais/DadosResidenciaisForm';
import { DadosBancariosForm } from './components/DadosBancarios/DadosBancariosForm';
import { Beneficiarios } from './components/Beneficiarios';
import { Dependentes } from './components/Dependentes';
import { ConferenciaForm } from './components/AtualizacaoCadastral/ConferenciaForm';
import { ProgressBar } from './components/AtualizacaoCadastral/progressBar';

export const MeusDadosPage = () => {
  const navigate = useNavigate();
  const dadosPessoaisService = new DadosPessoaisService();

  const participante = useSelector((state) => state.user);
  const { dadosSAPNaoTratados } = participante;
  const perfilInvestimento = useSelector(
    (state) => state.plan.perfilInvestimento.data
  );
  const patrocinadora = useSelector((state) => state.plan.patrocinadora.data);
  const {
    bloqueio_att_email: bloqueioEmail,
    att_dados_cadastrais: fluxoDiretoSAP,
    questionario_investidor: possuiPerfilInvestimento,
  } = useSelector((state) => state.configs.telasDisponiveis.data);

  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [conferenceCheck, setConferenceCheck] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [changedValues, setChangedValues] = useState({});

  const formSteps = [
    {
      label: 'Pessoais',
      component: (
        <DadosPessoaisForm
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          isActive={currentStep === 1}
        />
      ),
      validationSchema: dadosPessoaisSchema,
      isEditable: true,
    },
    {
      label: 'Contato',
      component: (
        <DadosContatoForm
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          isActive={currentStep === 2}
          bloqueioEmail={bloqueioEmail}
        />
      ),
      validationSchema: dadosContatoSchema,
      isEditable: true,
    },
    {
      label: 'Residenciais',
      component: (
        <DadosResidenciaisForm
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          isActive={currentStep === 3}
        />
      ),
      validationSchema: enderecoSchema,
      isEditable: true,
    },
    ...(participante.dadosPessoais.statusPlano !== 'Ativo' &&
    participante.dadosPessoais.statusPlano !== 'Desligado'
      ? [
          {
            label: 'Bancários',
            component: <DadosBancariosForm />,
            isEditable: false,
          },
          {
            label: 'Dependentes IRRF',
            component: <Dependentes loading={[setLoading, loading]} />,
            isEditable: false,
          },
        ]
      : []),
    ...(participante.dadosPessoais.statusPlano !== 'Pensionista'
      ? [
          {
            label: 'Beneficiários',
            component: <Beneficiarios />,
            isEditable: false,
          },
        ]
      : []),
    {
      label: 'Conferência das Informações',
      component: <ConferenciaForm changedValues={changedValues} />,
      isEditable: true,
      isConferenceForm: true,
    },
  ];
  const formStepsEditable = formSteps.filter((step) => step.isEditable);
  const formStepsViewable = formSteps.filter((step) => !step.isConferenceForm);

  const nextStep = () =>
    setCurrentStep((prev) => Math.min(prev + 1, formStepsEditable.length));

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const getChangedValues = (initialValues, currentValues) => {
    const { planoPrevidencia, ...filteredCurrentValues } = currentValues;
    const changedValuesInit = {};

    Object.keys(filteredCurrentValues).forEach((key) => {
      if (filteredCurrentValues[key] !== initialValues[key]) {
        changedValuesInit[key] = filteredCurrentValues[key];
      }
    });

    return changedValuesInit;
  };

  const [initialState, setInitialState] = useState({
    ...dadosSAPNaoTratados,
    celular: dadosSAPNaoTratados.celular
      ? dadosSAPNaoTratados.celular.replace(/\D/g, '')
      : '',
    telefoneFixo: dadosSAPNaoTratados.telefoneFixo
      ? dadosSAPNaoTratados.telefoneFixo.replace(/\D/g, '')
      : '',
    dtNascimento: dadosSAPNaoTratados.dtNascimento
      ? format(
          parse(dadosSAPNaoTratados.dtNascimento, 'yyyy-MM-dd', new Date()),
          'dd/MM/yyyy'
        )
      : 'dd/MM/yyyy',
    banco: participante.dadosPessoais.codBanco,
    agencia: participante.dadosPessoais.agencia,
    conta: participante.dadosPessoais.conta,
    digito: participante.dadosPessoais.digitoConta,
  });

  useEffect(() => {
    setInitialState({
      ...dadosSAPNaoTratados,
      celular: dadosSAPNaoTratados.celular
        ? dadosSAPNaoTratados.celular.replace(/\D/g, '')
        : '',
      telefoneFixo: dadosSAPNaoTratados.telefoneFixo
        ? dadosSAPNaoTratados.telefoneFixo.replace(/\D/g, '')
        : '',
      dtNascimento: dadosSAPNaoTratados.dtNascimento
        ? format(
            parse(dadosSAPNaoTratados.dtNascimento, 'yyyy-MM-dd', new Date()),
            'dd/MM/yyyy'
          )
        : 'dd/MM/yyyy',
      estadoCivil:
        dadosSAPNaoTratados.estadoCivil === 'Outros'
          ? ''
          : dadosSAPNaoTratados.estadoCivil,
      banco: participante.dadosPessoais.codBanco,

      agencia: participante.dadosPessoais.agencia,
      conta: participante.dadosPessoais.conta,
      digito: participante.dadosPessoais.digitoConta,
    });
  }, [dadosSAPNaoTratados, participante, isEdit]);

  return (
    <>
      <Helmet>
        <title>Forluz - Meus dados</title>
      </Helmet>
      <div className="row d-flex">
        <div className="col-4">
          <h1 className="page-title fw-bold mb-2">Meus dados</h1>
        </div>
        <div
          className="col-8 justify-content-end"
          style={{ textAlign: 'right' }}
        >
          {possuiPerfilInvestimento && perfilInvestimento?.Label && (
            <>
              <span className="text-orange fw-bold ts-10">
                {`PERFIL: ${perfilInvestimento.Label}`}
              </span>
              <br />
            </>
          )}

          <span className="text-muted ts-20">
            <strong>
              Plano {plans[participante.data.plano]}{' '}
              {participante.dadosPessoais.statusPlano === ''
                ? ''
                : ` - ${participante.dadosPessoais.statusPlano}`}{' '}
            </strong>
            {patrocinadora}
          </span>
        </div>
      </div>

      <hr />
      <div className="d-flex col-3 col-md-2 col-lg-1 justify-content-center">
        <Button
          className="justify-content-center mb-32 mt-32"
          transparent
          color="orange"
          icon="arrow_back"
          onClick={() => navigate(-1)}
        >
          Voltar
        </Button>
      </div>
      {!loading ? (
        <>
          <div className="row">
            <div className=" col-md-12">
              <div className="d-flex justify-content-center mt-4 mb-4">
                {isEdit
                  ? formStepsEditable.map((step, index) => {
                      const stepNumber = index + 1;
                      const isActive = stepNumber === currentStep;

                      return (
                        <div
                          key={step.label}
                          className="text-center mx-3"
                          style={{
                            color: isActive ? '#ff9800' : '#000',
                            fontWeight: isActive ? 'bold' : 'normal',
                            position: 'relative',
                          }}
                        >
                          <span>{`${stepNumber}. ${step.label}`}</span>
                          {isActive && (
                            <div
                              style={{
                                height: '2px',
                                backgroundColor: '#ff9800',
                                width: '100%',
                                marginTop: '4px',
                              }}
                            />
                          )}
                        </div>
                      );
                    })
                  : formStepsViewable.map((step, index) => {
                      const stepNumber = index + 1;
                      const isActive = stepNumber === currentStep;

                      return (
                        <div
                          role="button"
                          key={step.label}
                          tabIndex="0"
                          className="text-center mx-3"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              setCurrentStep(stepNumber);
                            }
                          }}
                          style={{
                            color: isActive ? '#ff9800' : '#000',
                            fontWeight: isActive ? 'bold' : 'normal',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => setCurrentStep(stepNumber)}
                        >
                          <span>{step.label}</span>
                          {isActive && (
                            <div
                              style={{
                                height: '2px',
                                backgroundColor: '#ff9800',
                                width: '100%',
                                marginTop: '4px',
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
              </div>
              {isEdit && (
                <ProgressBar
                  currentStep={currentStep}
                  totalSteps={formStepsEditable.length}
                />
              )}
            </div>
          </div>

          <div>
            <Formik
              enableReinitialize
              validateOnMount
              validationSchema={
                isEdit
                  ? formStepsEditable[currentStep - 1].validationSchema
                  : formStepsViewable[currentStep - 1].validationSchema
              }
              initialValues={{
                ...initialState,
                estadoCivil:
                  participante.dadosSAPNaoTratados.estadoCivil === 'Outros'
                    ? ''
                    : participante.dadosSAPNaoTratados.estadoCivil,
              }}
              onSubmit={(values) => {
                setLoading(true);

                const dadoscadastraisSAPAtualizados = {
                  email: values.email,
                  celular: values.celular.replace(/\D/g, ''),
                  telefoneFixo: values.telefoneFixo.replace(/\D/g, ''),
                  nome: values.nome,
                  dtNascimento: dadosSAPNaoTratados.dtNascimento,
                  nacionalidade: values.nacionalidade,
                  sexo: values.sexo,
                  estadoCivil: values.estadoCivil,
                  pai: values.pai,
                  mae: values.mae,
                  rg: values.rg,
                  orgEmissor: values.orgEmissor,
                  uf: values.uf,
                  cpf: values.cpf,
                  dtEmissao: values.dtEmissao,
                  endereco: values.endereco,
                  numero: values.numero,
                  complemento: values.complemento,
                  cep: values.cep,
                  bairro: values.bairro,
                  cidade: values.cidade,
                  estado: values.estado,
                  numBanco: values.numBanco,
                  agencia: values.agencia,
                  conta: `${values.conta}-${values.digito}`,
                  matriculaFalecido: values.matriculaFalecido,
                  dependente: values.dependente,
                  conjuge: values.conjuge,
                  localNascimento: values.localNascimento,
                  estadoNasc: values.estadoNasc,
                  pais: values.pais,
                  status: values.status,
                  dtAdminissao: values.dtAdminissao,
                  dtDesligamento: values.dtDesligamento,
                  motivoDeslig: values.motivoDeslig,
                  dtAfastamento: values.dtAfastamento,
                  motivoEncerramento: values.motivoEncerramento,
                  tpBeneficio: values.tpBeneficio,
                  dtEncerramentoBeneficio: values.dtEncerramentoBeneficio,
                  motivoAfastamento: values.motivoAfastamento,
                  planoPrevidencia: '',
                  situacaoParticipante: values.situacaoParticipante,
                };

                dadosPessoaisService
                  .atualizacaoCadastral(
                    changedValues,
                    dadoscadastraisSAPAtualizados,
                    participante.data.matricula,
                    participante.data.token,
                    fluxoDiretoSAP
                  )
                  .then((response) => {
                    if (response.data.sucesso === true) {
                      setLoading(false);
                      navigate(SOLICITACAO_SUCESSO, {
                        state: {
                          data: response.data.data,
                          protocolo: response.data.protocolo,
                          status: response.data.status,
                          nomeRequerimento: 'Atualização Cadastral',
                        },
                      });
                    } else {
                      setLoading(false);
                      setModalIsOpen(true);
                    }
                  })
                  .catch(() => {
                    setLoading(false);
                    setModalIsOpen(true);
                  });
              }}
            >
              {({ handleSubmit, isValid, values }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="card">
                    <div className="card-body">
                      {isEdit ? (
                        <>
                          {formStepsEditable[currentStep - 1].component}

                          <div className="d-flex justify-content-end mt-4">
                            {currentStep < formStepsEditable.length ? (
                              <>
                                {currentStep > 1 && (
                                  <div className="col-12 col-md-3">
                                    <Button
                                      type="button"
                                      disabled={!isValid}
                                      onClick={prevStep}
                                      color="orange"
                                      transparent
                                    >
                                      Voltar
                                    </Button>
                                  </div>
                                )}

                                <div className="col-12 col-md-3 ms-2">
                                  <Button
                                    type="button"
                                    disabled={!isValid}
                                    onClick={() => {
                                      const modifiedValues = getChangedValues(
                                        initialState,
                                        values
                                      );
                                      setChangedValues(modifiedValues);
                                      nextStep();
                                    }}
                                  >
                                    Próximo
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="d-flex col-8 align-items-center">
                                  <Checkbox
                                    className="beneficiario_checkbox"
                                    defaultChecked={conferenceCheck}
                                    onChange={() =>
                                      setConferenceCheck(!conferenceCheck)
                                    }
                                  />
                                  <p className="text-muted ts-16 ">
                                    Conferi as informações e meus dados estão
                                    corretos
                                  </p>
                                </div>

                                <div className="d-flex col-4 justify-content-end gap-3">
                                  {currentStep > 1 && (
                                    <>
                                      <div className="col-4">
                                        <Button
                                          type="button"
                                          onClick={() => {
                                            prevStep();
                                            setConferenceCheck(false);
                                          }}
                                          color="orange"
                                          transparent
                                        >
                                          Voltar
                                        </Button>
                                      </div>
                                      <div className="col-7">
                                        <Button
                                          disabled={
                                            !conferenceCheck ||
                                            !Object.keys(changedValues).length
                                          }
                                          type="submit"
                                        >
                                          Confirmar Solicitação
                                        </Button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {formStepsViewable[currentStep - 1].component}
                          {formStepsViewable[currentStep - 1].isEditable &&
                            fluxoDiretoSAP && (
                              <div className="d-flex justify-content-end mt-4">
                                <div className="col-12 col-md-3 ms-2">
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      setIsEdit(true);
                                      setCurrentStep(1);
                                    }}
                                  >
                                    Editar
                                  </Button>
                                </div>
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </>
      ) : (
        <LoadingIndicator />
      )}

      <FModal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)}>
        <>
          <Icons path={mdiCheckCircleOutline} color="var(--success)" size={2} />
          <h1 className="ts-20 fw-bold mb-1">Dados atualizados</h1>
          <p className="ts-16">
            O processamento está previsto para até 2 dias úteis.
          </p>
          <Button
            className="mt-32 col-4"
            onClick={() => {
              setModalIsOpen(false);
            }}
          >
            Ok
          </Button>
        </>
      </FModal>
    </>
  );
};
